import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Container, Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { Facebook, LinkedIn, Email } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import PageHeaders from '../../../../Utils/PageHeaders';
import { fetchNewsletter, fetchNewsletters } from '../api';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PartnersSection from '../../Home/components/PartnersSection';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
  metadata: {
    color: '#64748B',
    fontSize: '14px',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 24,
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
    fontWeight: 600,
    color: '#1E293B',
    marginBottom: theme.spacing(4),
    lineHeight: 1.3,
  },
  content: {
    '& img': {
      width: '100%',
      height: 280,
      objectFit: 'cover',
      borderRadius: 8,
      marginBottom: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        height: 400,
      },
    },
    '& p': {
      marginBottom: theme.spacing(2),
      lineHeight: 1.6,
    },
  },
  introContent: {
    marginBottom: theme.spacing(4),
    '& p': {
      fontSize: '18px',
      color: '#1E293B',
      lineHeight: 1.6,
    },
  },
  gridContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  storyBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'stretch',
      gap: theme.spacing(4),
    },
  },
  storyImg: {
    width: '100% !important',
    height: 'auto !important',
    maxHeight: 300,
    objectFit: 'cover',
    borderRadius: 8,
    margin: '0 !important',
    [theme.breakpoints.up('md')]: {
      width: '320px !important',
      height: '200px !important',
      flexShrink: 0,
    },
  },
  storyText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '& p:first-of-type': {
      fontWeight: 600,
      color: '#1E293B',
      marginTop: 0,
    },
    '& p:last-of-type': {
      marginBottom: 0,
    },
  },
  sourceLine: {
    marginTop: 'auto !important',
    color: '#64748B !important',
    fontSize: '14px !important',
  },
  footerSource: {
    color: '#94A3B8',
    fontSize: '14px',
    marginTop: theme.spacing(2),
  },
  hr: {
    border: 0,
    height: 2,
    backgroundColor: '#94A3B8',
    margin: theme.spacing(2, 0),
  },
  signoffTitle: {
    color: '#1E293B',
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  signoffSub: {
    color: '#1E293B',
    fontSize: '14px',
  },
  vision: {
    textAlign: 'center',
    color: '#1E293B',
    fontSize: '16px',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  subtext: {
    textAlign: 'center',
    color: '#64748B',
    fontSize: '13px',
    marginTop: theme.spacing(0.5),
  },
  disclaimer: {
    color: '#1E293B',
    fontSize: '15px',
    marginTop: theme.spacing(2),
  },
  socialRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    justifyContent: 'center',
  },
  copyrightBar: {
    backgroundColor: '#3F3F46',
    color: '#E5E7EB',
    textAlign: 'center',
    padding: theme.spacing(1),
    borderRadius: 2,
    fontSize: '12px',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  unsubscribe: {
    textAlign: 'center',
    marginTop: theme.spacing(1),
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

const splitHtml = (html) => {
  if (!html) return ['', ''];
  const match = html.match(/<\/p>/i);
  if (!match) return [html, ''];
  const index = match.index + 4;
  return [html.substring(0, index), html.substring(index)];
};

const wrapStories = (html, classes) => {
  if (!html) return '';
  const storyRegex = /(<img[^>]*>)([\s\S]*?)(?=<img|$)/gi;
  return html.replace(storyRegex, (match, img, text) => {
    // Inject class for the image
    const imgWithClass = img.replace(/<img/i, `<img class="${classes.storyImg}"`);
    // Inject class for the source line
    const textWithSource = text.replace(/<p>Source:/gi, `<p class="${classes.sourceLine}">Source:`);
    return `<div class="${classes.storyBlock}">${imgWithClass}<div class="${classes.storyText}">${textWithSource}</div></div>`;
  });
};

const NewsletterDetails = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        let data = await fetchNewsletter(slug);
        if (!data) {
          const list = await fetchNewsletters();
          data = list.find((n) => String(n.slug) === String(slug) || String(n.id) === String(slug)) || null;
        }
        setItem(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load newsletter');
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <PageHeaders
        title={`${item?.heading || 'Newsletter'} - Plootus Newsletters`}
        description={item?.description || 'Read this insightful newsletter from Plootus about financial planning and retirement.'}
        keywords={`Plootus newsletter, ${item?.heading || ''}, financial advice, retirement planning, investment tips`}
        ogType="article"
        ogUrl={`https://www.plootus.com/newsletters/${slug}`}
        ogImage={item?.imageUrl || item?.image}
        canonicalUrl={`https://www.plootus.com/newsletters/${slug}`}
        additionalMeta={{
          'article:published_time': item?.date,
        }}
      />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": item?.heading || '',
          "description": item?.description || '',
          "image": item?.imageUrl || item?.image || '',
          "datePublished": item?.date,
          "author": {
            "@type": "Organization",
            "name": "Plootus"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Plootus",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.plootus.com/logo.png"
            }
          }
        })}
      </script>
      {loading ? (
        <Box className={classes.loader}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">Error: {error}</Typography>
      ) : !item ? (
        <Typography>Newsletter not found</Typography>
      ) : (
        <Box>
          <Typography className={classes.metadata}>
            {item?.date ? `Published on ${moment(item.date).format('MMMM D, YYYY')}` : ''}
          </Typography>
          <Typography variant="h1" className={classes.title}>
            {item?.heading || ''}
          </Typography>
          {(() => {
            const raw = item?.content || item?.contentHtml || item?.html || item?.htmlData || '';
            const fullHtml = (typeof raw === 'string' && /<[^>]+>/.test(raw)) ? raw : (raw ? String(raw).replace(/\n/g, '<br />') : (item?.description || ''));
            const [part1, part2] = splitHtml(fullHtml);
            return (
              <>
                <Box
                  className={classes.introContent}
                  dangerouslySetInnerHTML={{ __html: part1 }}
                />

                <PartnersSection titleDisabled={true} rootPadding="24px 0" />

                <Box
                  className={classes.gridContent}
                  dangerouslySetInnerHTML={{ __html: wrapStories(part2, classes) }}
                />
              </>
            );
          })()}
          {item?.source ? (
            <>
              <Typography className={classes.footerSource}>
                {`Source: ${item.source}`}
              </Typography>
              <Box component="hr" className={classes.hr} />
            </>
          ) : null}
          {/* <Typography className={classes.signoffTitle}>
            Have a great week ahead!
          </Typography>
          <Typography className={classes.signoffSub}>
            Co-founders, Plootus
          </Typography> */}
          <Box component="hr" className={classes.hr} />
          <Typography className={classes.vision}>
            Our vision is to democratize retirement planning and make it available to everyone
          </Typography>
          <Typography className={classes.subtext}>
            Feel free to reach out to us if you have any questions{' '}
            <a href="/faqs#contact" style={{ color: '#4361EE' }}>here</a>
          </Typography>
          <Typography className={classes.disclaimer}>
            Disclaimer: Plootus (an SEC-registered investment advisor) may be compensated for third-party referrals, which are for informational purposes only and not endorsements.
          </Typography>
          <div className={classes.socialRow}>
            <IconButton href="#" aria-label="Facebook" size="small" sx={{ bgcolor: '#3b5998', color: 'white' }}>
              <Facebook fontSize="small" />
            </IconButton>
            <IconButton href="#" aria-label="X" size="small" sx={{ bgcolor: '#000000', color: 'white' }}>
              <Box component="span" sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1 }}>X</Box>
            </IconButton>
            <IconButton href="#" aria-label="LinkedIn" size="small" sx={{ bgcolor: '#0077b5', color: 'white' }}>
              <LinkedIn fontSize="small" />
            </IconButton>
            <IconButton href="/faqs#contact" aria-label="Email" size="small" sx={{ bgcolor: '#111827', color: 'white' }}>
              <Email fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.copyrightBar}>
            {`Copyright © 2018-${new Date().getFullYear()} Plootus.com, All Rights Reserved`}
          </div>
          <div className={classes.unsubscribe}>
            <a href="/confirm-unsubscribe" style={{ color: '#4361EE' }}>Unsubscribe</a>
          </div>
        </Box>
      )}
    </Container>
  );
};

export default NewsletterDetails;
